import { Component, ViewChild, OnInit, inject } from '@angular/core';
import { MatTableModule, MatTableDataSource } from '@angular/material/table';
import { MatSortModule, MatSort } from '@angular/material/sort';
import { MatPaginatorModule, MatPaginator } from '@angular/material/paginator';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatTooltip } from "@angular/material/tooltip";
import { CommonModule } from '@angular/common';
import { AdminService } from '@adgate/modules/admin/services/admin-service';
import { AuthenticationService } from '@adgate/common/services/authentication-service';
import { AdminPanelModule } from '@adgate/modules/admin/types/admin-panel-module.interface';
import { RESTApplicationResponse } from '@adgate/common/types/rest-application-response.type';
import { DialogService } from '@adgate/common/services/dialog-service';
import { Role } from '@adgate/modules/admin/types/role.type';
import { User } from '@adgate/modules/admin/types/user.type';
import { filter, switchMap } from 'rxjs';
import { RoleEditDialogComponent } from '@adgate/modules/admin/dialogs/role-edit-dialog-component/role-edit-dialog-component';
import { QuickSearch } from '@adgate/common/interfaces/quick-search.interface';

@Component({
  selector: 'app-admin-panel-roles-component',
  imports: [
    CommonModule,
    MatTableModule,
    MatSortModule,
    MatPaginatorModule,
    MatCheckboxModule,
    MatTooltip
  ],
  templateUrl: './admin-panel-roles-component.html',
  styleUrl: './admin-panel-roles-component.scss',
})
export class AdminPanelRolesComponent implements OnInit, AdminPanelModule, QuickSearch
{
  @ViewChild(MatSort) sort!: MatSort;
  @ViewChild(MatPaginator) paginator!: MatPaginator;

  private authService: AuthenticationService = inject(AuthenticationService);
  private adminService: AdminService = inject(AdminService);
  private dialogService: DialogService = inject(DialogService);

  displayedColumns: string[] = ['name', 'actions'];
  dataSource: MatTableDataSource<Role> = new MatTableDataSource<Role>();

  constructor()
  {
    
  }

  ngOnInit(): void
  {
    this.onRefresh();
  }

  ngAfterViewInit()
  {
    this.dataSource.sortingDataAccessor = this.getTableDataAccessor();
    this.dataSource.sort = this.sort;
    this.dataSource.paginator = this.paginator;
  }

  onRefresh(): void 
  {
    let attr = this.authService.getUserAttributes();

    this.dialogService.showProgress();
    this.adminService.getRoles((attr.type != User.TYPE_MULTI)).subscribe({
      next: (r:RESTApplicationResponse) => {
        this.dataSource.data = r.data;
        this.dialogService.hideProgress();
      },
      error: ((err:Error) => {
        this.dialogService.openErrorDialog(err.message);
      })
    });
  }

  getQuickSearchPlaceholder(): string 
  {
    return 'Search Roles';
  }

  onQuickSearch(filter: string | null)
  {
    this.dataSource.filter = filter!;
  }

  onNewRole(): void
  {
    this.onEditRole(new Role());
  }

  onEditRole(role: Role): void
  {
    let successMessage: string;
    let attr = this.authService.getUserAttributes();

    const dialogRef = this.dialogService.openAnyDialog(RoleEditDialogComponent, {
      data: role,
      width: '700px',
      maxWidth: '90vw',
      disableClose: true
    });

    dialogRef.afterClosed().pipe(
      filter((result: Role | null) => result !== null),
      switchMap((result: Role) => {
        this.dialogService.showProgress();
        return this.adminService.updateRole(result);
      }),
      switchMap((r: RESTApplicationResponse) => {
        successMessage = r.message;
        return this.adminService.getRoles((attr.type != User.TYPE_MULTI));
      })
    ).subscribe({
      next: (r: RESTApplicationResponse) => {
        this.dataSource.data = r.data;
        this.dialogService.openInfoDialog(successMessage);
      },
      error: (err: Error) => {
        this.dialogService.openErrorDialog(err.message);
      }
    });
  }

  onDeleteRole(row: Role): void
  {
    const dialogRef = this.dialogService.openConfirmDialog(
      `Delete role "${row.description}"?`,
      {
        title: 'Delete Role',
        confirmText: 'Delete',
        cancelText: 'Cancel',
        severity: 'danger'
      }
    );

    dialogRef.afterClosed().pipe(
      filter((confirmed) => confirmed === true),
      switchMap(() => {
        this.dialogService.showProgress();
        return this.adminService.deleteRole(row.id);
      })
    ).subscribe({
      next: (r: RESTApplicationResponse) => {
        this.dialogService.openInfoDialog(r.message);
        this.onRefresh();
      },
      error: (err: Error) => {
        this.dialogService.openErrorDialog(err.message);
      }
    });
  }

  getTableDataAccessor(): any
  {
    return (item: any, property: any) => {
      switch(property) {
        case 'name': return item.fullName;
        case 'authType': return item.authMethod;
        case 'office': return (item.office != null ? item.office.description : '');
        case 'agency': return (item.agency != null ? item.agency.description : '');
        default: return item[property];
      }
    };
  }
}
